import { createAsyncThunk } from '@reduxjs/toolkit';
import {} from '@constants';
import {} from 'utils';
import { AppStateType } from 'reducers';
import {
  FetchAvailableDumpFilesParams,
  DeleteRealmsParams,
  FetchSandboxParams,
  CreateDumpFileParams,
  InitializeSandboxParams,
  FetchSandboxMembersCsv,
  UpdateSandboxParams,
  FetchSandboxCreatorsParams,
  FetchSandboxSuccessResponse,
  DeleteRealmPayload,
} from 'SandboxModule/types';
import * as SandboxServices from 'SandboxModule/services';
import { User } from 'AuthModule/reducers';
import ApiClientManager from 'core/apiClient';

export const deleteRealms = createAsyncThunk(
  'deleteRealms',
  async (params: DeleteRealmsParams, { dispatch, getState, rejectWithValue }) => {
    const {
      authReducer: { user },
    } = getState() as AppStateType;
    const auth_token = user?.auth_token;
    const { onSuccess, onFailure, realmInfo, reasonToArchive } = params;

    try {
      if (!auth_token || !user) throw new Error('Not Authenticated');

      const meta: DeleteRealmPayload['meta'] = { name: user.account.name };
      // Single request with all selected realm infos
      const response = await SandboxServices.deleteRealm({
        params: { realmInfo, reasonToArchive, meta },
        token: auth_token,
      });

      onSuccess?.({ response });
      return response;
    } catch (e) {
      onFailure?.({ error: e });
      return rejectWithValue(e);
    }
  },
);

// Track in-flight requests to prevent duplicate API calls
const pendingFetchSandboxRequests = new Map<string, Promise<FetchSandboxSuccessResponse>>();

export const fetchSandboxes = createAsyncThunk<FetchSandboxSuccessResponse, FetchSandboxParams>(
  'fetchSandboxes',
  async (
    {
      includeArchived,
      tenant,
      createdAtStartDate,
      createdAtEndDate,
      realmIdSearchKey,
      teamNameSearchKey,
      statuses,
      creators,
      tags,
      isoStateId,
      realmTypes,
      meta,
    }: FetchSandboxParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    // Deduplicate requests by isoStateId + filter params
    // If there's already a pending request for the same params, return that promise.
    // Filter params must be part of the key, otherwise a search submitted while a
    // fetch is in flight would reuse the stale promise and drop the new filters.
    const requestKey = [
      isoStateId,
      meta?.tenantName || '',
      meta?.realmId || '',
      JSON.stringify({
        includeArchived,
        tenant,
        createdAtStartDate,
        createdAtEndDate,
        realmIdSearchKey,
        teamNameSearchKey,
        statuses,
        creators,
        tags,
        realmTypes,
      }),
    ].join('-');
    const existingRequest = pendingFetchSandboxRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    // Create the request promise
    const requestPromise = (async () => {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const { onSuccess, onFailure, realmId, tenantName } = meta || {};

      const config = {
        params: {
          includeArchived,
          tenant,
          createdAtStartDate,
          createdAtEndDate,
          realmIdSearchKey,
          teamNameSearchKey,
          statuses,
          creators,
          tags,
          realmTypes,
        },
        headers: ApiClientManager.buildUrlHeaders({ realmId, tenantName }),
      };

      try {
        const response = await SandboxServices.fetchSandboxes({}, config, auth_token as string);
        if (onSuccess) {
          onSuccess({ response: response.data as FetchSandboxSuccessResponse });
        }
        return response.data as FetchSandboxSuccessResponse;
      } catch (e) {
        if (onFailure) {
          onFailure({ error: e });
        }
        throw e; // Re-throw to propagate error
      } finally {
        // Clean up the pending request after completion (success or error)
        pendingFetchSandboxRequests.delete(requestKey);
      }
    })();

    // Store the promise so duplicate requests can reuse it
    pendingFetchSandboxRequests.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);
export const fetchAvailableDumpFiles = createAsyncThunk(
  'fetchAvailableDumpFiles',
  async (params: FetchAvailableDumpFilesParams, { dispatch, getState, rejectWithValue }) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      if (user) {
        const auth_token = user.auth_token;
        const response = await SandboxServices.getAvailableDumpFiles({
          params,
          token: auth_token,
        });
        return response.data;
      }
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const createDumpFile = createAsyncThunk(
  'createDumpFile',
  async (
    { onSuccess, fileName, onFailure }: CreateDumpFileParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    const {
      authReducer: { user },
    } = getState() as AppStateType;
    const auth_token = user?.auth_token;

    try {
      const data = {
        fileName,
      };
      const response = await SandboxServices.createDumpFile({}, data, {}, auth_token);

      if (onSuccess) {
        onSuccess({ response });
      }
      return response.data;
    } catch (error) {
      if (onFailure) {
        onFailure({ error });
      }
      return rejectWithValue(error);
    }
  },
);

export const initializeSandbox = createAsyncThunk(
  'initializeSandbox',
  async (
    { onFailure, onSuccess, realmId, tenant }: InitializeSandboxParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await SandboxServices.initializeSandbox(
        {},
        { realmId, tenant },
        {},
        auth_token,
      );

      if (onSuccess) {
        onSuccess({ response });
      }
      return response.data;
    } catch (error) {
      if (onFailure) {
        onFailure({ error });
      }
      return rejectWithValue(error);
    }
  },
);

export const fetchSandboxMembersCsv = createAsyncThunk(
  'fetchSandboxMembersCsv',
  async (
    { onFailure, onSuccess, teamId }: FetchSandboxMembersCsv,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await SandboxServices.getSandboxMembersCsvUrl(
        {},
        { team_id: teamId },
        {},
        auth_token,
      );

      if (onSuccess) {
        onSuccess({ response });
      }
      return response.data;
    } catch (error) {
      if (onFailure) {
        onFailure({ error });
      }
      return rejectWithValue(error);
    }
  },
);

export const updateSandbox = createAsyncThunk(
  'updateSandbox',
  async (
    { onFailure, onSuccess, realmId, teamName, creator, tags }: UpdateSandboxParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await SandboxServices.updateSandbox(
        {},
        { realmId, teamName, creator, tags },
        {},
        auth_token,
      );

      if (onSuccess) {
        onSuccess({ response });
      }
      return response;
    } catch (error) {
      if (onFailure) {
        onFailure({ error });
      }
      return rejectWithValue(error);
    }
  },
);

// Probably need to put in another module though
// export const replaceMembers = createAsyncThunk(
//   "replaceMembers",
//   async (params, { dispatch, getState, rejectWithValue }) => {
//     try {
//       const {
//         authReducer: { user },
//       } = getState() as AppStateType;
//       const auth_token = user?.auth_token;
//       const response = {};
//       return response;
//     } catch (e) {
//       return rejectWithValue(e);
//     }
//   }
// );

export const fetchSandboxCreators = createAsyncThunk(
  'fetchSandboxCreators',
  async (
    { onFailure, onSuccess }: FetchSandboxCreatorsParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await SandboxServices.getSandboxCreators({}, {}, auth_token);
      if (onSuccess) {
        onSuccess({ response });
      }
      return response;
    } catch (error) {
      if (onFailure) {
        onFailure({ error });
      }
      return rejectWithValue(error);
    }
  },
);
